import { api, apiErrorMessage, ApiError } from '@ddone/api-client';
import type { EntityReference } from '@ddone/shared-types';
import { EmptyState, Input, Modal } from '@ddone/ui';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowUpRight, Compass, FileSearch, Search } from 'lucide-react';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  guidedActions,
  guidedActionsForNavigation,
  normalizeSearchText,
  searchGuidedActions
} from '../help/guided-actions';
import type { NavigationItem } from './navigation';

interface SearchResult extends EntityReference {
  href: string;
  description?: string;
}

export function CommandPalette({
  open,
  onOpenChange,
  navigation,
  hasPermission
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  navigation: NavigationItem[];
  hasPermission(permission?: string): boolean;
}) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query.trim());
  const navigate = useNavigate();
  const results = useQuery({
    queryKey: ['global-search', deferredQuery],
    queryFn: () =>
      api.request<SearchResult[]>(`/search?q=${encodeURIComponent(deferredQuery)}&limit=12`),
    enabled: open && deferredQuery.length >= 2,
    staleTime: 10_000,
    retry: (failureCount, error) => !isSearchEndpointUnavailable(error) && failureCount < 1
  });
  const shortcuts = useMemo(
    () =>
      navigation.filter((item) =>
        normalizeSearchText(item.label).includes(normalizeSearchText(deferredQuery))
      ),
    [deferredQuery, navigation]
  );
  const availableGuides = useMemo(
    () =>
      guidedActionsForNavigation(
        new Set(navigation.map((item) => item.id)),
        guidedActions,
        hasPermission
      ),
    [hasPermission, navigation]
  );
  const matchingGuides = useMemo(
    () =>
      (deferredQuery
        ? searchGuidedActions(availableGuides, deferredQuery)
        : availableGuides.slice(0, 4)
      ).slice(0, 6),
    [availableGuides, deferredQuery]
  );
  const searchUnavailable = isSearchEndpointUnavailable(results.error);
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);
  const go = (href: string) => {
    onOpenChange(false);
    void navigate({ to: href });
  };
  const openGuide = (id: string) => {
    onOpenChange(false);
    void navigate({ to: '/help', hash: id });
  };
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Cerca di cosa hai bisogno"
      description="Trova una pagina, un dato salvato oppure un percorso guidato."
      size="large"
    >
      <div className="dd-command-search">
        <Search size={19} />
        <Input
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Es. creare un cliente, registrare ore, aggiungere una scadenza…"
          aria-label="Ricerca globale"
        />
      </div>
      <div className="dd-command-results">
        {shortcuts.length ? (
          <section>
            <h3>Pagine</h3>
            {shortcuts.map((item) => (
              <button key={item.id} type="button" onClick={() => go(item.href)}>
                <item.icon size={17} />
                <span>{item.label}</span>
                <ArrowUpRight size={15} />
              </button>
            ))}
          </section>
        ) : null}
        {matchingGuides.length ? (
          <section>
            <h3>Azioni guidate</h3>
            {matchingGuides.map((action) => (
              <button key={action.id} type="button" onClick={() => openGuide(action.id)}>
                <Compass size={17} />
                <span>
                  <strong>{action.title}</strong>
                  <small>{action.description}</small>
                </span>
                <ArrowUpRight size={15} />
              </button>
            ))}
          </section>
        ) : null}
        {results.data?.length ? (
          <section>
            <h3>Risultati</h3>
            {results.data.map((result) => (
              <button
                key={`${result.moduleId}:${result.entityId}`}
                type="button"
                onClick={() => go(result.href)}
              >
                <FileSearch size={17} />
                <span>
                  <strong>{result.displayLabel}</strong>
                  <small>{result.description ?? entityTypeLabel(result.entityType)}</small>
                </span>
                <ArrowUpRight size={15} />
              </button>
            ))}
          </section>
        ) : null}
        {searchUnavailable ? (
          <p className="dd-command-notice" role="status">
            La ricerca nei dati salvati non è disponibile in questo ambiente. Puoi continuare con
            pagine e azioni guidate.
          </p>
        ) : null}
        {results.isError && !searchUnavailable ? (
          <p className="dd-inline-error" role="alert">
            {apiErrorMessage(results.error)}
          </p>
        ) : null}
        {deferredQuery.length >= 2 &&
        !results.isFetching &&
        !results.data?.length &&
        !shortcuts.length &&
        !matchingGuides.length &&
        (!results.isError || searchUnavailable) ? (
          <EmptyState
            title="Nessun risultato"
            description="Prova con un nome o un termine diverso."
          />
        ) : null}
        {!deferredQuery ? (
          <p className="dd-command-hint">
            Scrivi un obiettivo oppure scegli una pagina o una guida. I risultati rispettano moduli
            e permessi del workspace.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export function isSearchEndpointUnavailable(error: unknown): boolean {
  return error instanceof ApiError && error.problem.status === 404;
}

function entityTypeLabel(entityType: string): string {
  const labels: Record<string, string> = {
    client: 'Cliente',
    opportunity: 'Opportunità',
    project: 'Progetto',
    note: 'Nota',
    document: 'Nota',
    task: 'Attività',
    time_entry: 'Registrazione di tempo',
    billing_item: 'Voce di scadenza',
    billing_occurrence: 'Scadenza',
    expense: 'Spesa'
  };
  return labels[entityType.toLocaleLowerCase('it')] ?? 'Dato salvato';
}
